import { createSelector, createSlice } from "@reduxjs/toolkit";

// `updateProvider` from PatientSlice, matched by type: importing PatientSlice
// here would close an import cycle (PatientSlice -> apiService -> Store ->
// this slice) and leave Store with an undefined reducer.
const UPDATE_PROVIDER = "patientdetails/updateProvider";

// Reschedule mode (STN-427). Entered from a `?rt=<token>` link; the token is
// opaque and is resolved server-side by GET rescheduleLink (plan §4.3/§4.5).
// `token` + `data` are persisted (see Store.js). `invalidReason` is not, so a
// reload after an expired link lands in the normal funnel. `data` carries
// `purpose` and `originalInactive`, so the inactive-original notice (derived
// from `data`) survives a reload too.
const initialState = {
  token: null,
  data: null,
  // null | "invalid" (404 from GET rescheduleLink, a link for another client,
  // or 409 at booking) | "error" (network, 5xx, any other failure)
  invalidReason: null,
  // `clinicPhone` from the resolved link, kept for the expired page once
  // `data` is dropped. Not persisted, like `invalidReason`.
  invalidPhone: null,
  // The provider the patient has on Find Appointment right now, as
  // `{ providerId }` (`providerId` null for "Any Provider"); null until the
  // first one. Persisted, unlike `Patientdetails.provider`, so a reload keeps
  // the patient's choice instead of falling back to the link's provider.
  providerChoice: null,
};

const rescheduleSlice = createSlice({
  name: "reschedule",
  initialState,
  reducers: {
    rescheduleResolved: (state, action) => {
      state.token = action.payload?.token || null;
      state.data = action.payload?.data || null;
      state.invalidReason = null;
      state.invalidPhone = null;
      state.providerChoice = null;
    },
    // Payload: a reason string, or `{ reason, clinicPhone }` when the link
    // had resolved for this client (409 at booking time).
    rescheduleInvalid: (state, action) => {
      const payload = action.payload;
      const isObject = payload && typeof payload === "object";
      state.token = null;
      state.data = null;
      state.invalidReason = (isObject ? payload.reason : payload) || "invalid";
      state.invalidPhone = (isObject && payload.clinicPhone) || null;
      state.providerChoice = null;
    },
    clearReschedule: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      (action) => action.type === UPDATE_PROVIDER,
      (state, action) => {
        if (!state.token || !state.data || !action.payload) return;
        state.providerChoice = { providerId: action.payload.providerId ?? null };
      },
    );
  },
});

export const { rescheduleResolved, rescheduleInvalid, clearReschedule } =
  rescheduleSlice.actions;

export const selectRescheduleToken = (state) => state?.reschedule?.token;
export const selectRescheduleData = (state) => state?.reschedule?.data;
export const selectRescheduleInvalidReason = (state) =>
  state?.reschedule?.invalidReason;
export const selectRescheduleInvalidPhone = (state) =>
  state?.reschedule?.invalidPhone;
export const selectRescheduleProviderChoice = (state) =>
  state?.reschedule?.providerChoice;
export const selectIsRescheduleMode = (state) =>
  Boolean(state?.reschedule?.token && state?.reschedule?.data);

// The same shape a personal link carries in
// `Patientdetails.patientFromShortUrlData`, so the provider page pre-fills the
// original provider through its existing default-provider path. Once the
// patient has a provider on the page, that choice is the default instead, so
// a reload doesn't snap back to the original (`undefined` = Any Provider). Nothing is
// locked (Ryan, 2026-10-13): provider, service and location can all be
// changed, and the backend decides between a reschedule (same service) and a
// new booking that cancels the original (different service).
export const selectRescheduleLinkDefaults = createSelector(
  [selectIsRescheduleMode, selectRescheduleData, selectRescheduleProviderChoice],
  (isRescheduleMode, data, providerChoice) =>
    isRescheduleMode
      ? {
          providerId: providerChoice
            ? (providerChoice.providerId ?? undefined)
            : (data?.providerId ?? undefined),
          // Either may be null on the link: no pre-fill for that field.
          appointmentTypeId: data?.appointmentTypeId ?? undefined,
          isProviderLocked: false,
          isappointmentTypeLocked: false,
          isLocationLocked: false,
          isPersonalLink: 0,
        }
      : null,
);

export default rescheduleSlice.reducer;
